import Axios from 'axios';
import qs from 'qs';
import { stringifyPaginationV3 } from '@/_helpers/utils.js';
import { testEnvironments } from '@/_helpers/constants.js';

let integrationHost = process.env.INTEGRATION_HOST;

if (testEnvironments.includes(window.location.host)) {
  integrationHost = process.env.INTEGRATION_HOST_TEST;
}

if (window.location.hostname.indexOf('vcv.ai') > 0) {
  integrationHost = process.env.INTEGRATION_HOST_AI;
}

const getDefaultState = () => ({
  isHrmLoading: false,
  isHrmIntegrationStatus: false,
  integrationInterval: null,
  integrationIntervalCount: 0,
  // vacancy settings
  isConnectionLoading: false,
  hrmProjects: [],
  hrmProjectsPagination: {
    _page: 1,
    _page_count: 0,
    _total_items: 0,
    _limit: 20
  },
  hrmManagers: [],
  projectsHrmFilters: {
    title: '',
    region: [],
    author: []
  }
});

const state = getDefaultState();

const getters = {
  vacanciesAlreadyConnected: state => {
    const connectedVacancies = state.hrmProjects.filter(x => x.isActive).map(x => x._embedded.vacancy.vcv_vacancy_id);

    if (connectedVacancies.length > 0) {
      return connectedVacancies;
    }

    return [];
  }
};

const mutations = {
  SET_IS_HRM_LOADING: (state, payload) => {
    state.isHackerRankLoading = payload;
  },

  STOP_INTERVAL_INTEGRATION_CHECK: state => {
    clearInterval(state.integrationInterval);
    state.integrationIntervalCount = 0;
  },

  INCREMENT_INTEGRATION_INTERVAL_COUNT: state => {
    state.integrationIntervalCount += 1;
  },

  SET_INTEGRATION_INTERVAL: (state, payload) => {
    state.integrationInterval = payload;
  },

  SET_HRM_INTEGRATION_STATUS: (state, payload) => {
    state.isHrmIntegrationStatus = payload;
  },

  SET_CONNECTION_LOADING: (state, payload) => {
    state.isConnectionLoading = payload;
  },

  SET_HRM_PROJECTS: (state, payload) => {
    state.hrmProjects = payload;
  },

  SET_HRM_MANAGERS: (state, payload) => {
    state.hrmManagers = payload;
  },

  SET_FILTER_PROPERTY: (state, { property, value }) => {
    state.projectsHrmFilters[property] = value;
  },

  SET_HRM_PROJECTS_PAGINATION: (state, { property, value }) => {
    state.hrmProjectsPagination[property] = value;
  },

  TOGGLE_HRM_TO_VACANCY: (state, projectId) => {
    const projectIndex = state.hrmProjects.findIndex(x => x.id == projectId);

    if (projectIndex >= 0) {
      state.hrmProjects[projectIndex].isActive = !state.hrmProjects[projectIndex].isActive;
    }
  },

  RESET_INTEGRATIONS_HEADHUNTER: state => {
    Object.assign(state, getDefaultState());
  }
};

const actions = {
  async grantAccessToHrm({ commit, dispatch, rootGetters }) {
    // console.log(window.location.host);
    commit('SET_IS_HRM_LOADING', true);
    commit('SET_HRM_INTEGRATION_STATUS', false);

    // Запрашиваем список интеграций (в новом списке должен появится access_token для каждой интеграции)
    await dispatch('integrations/fetchIntegrationsInstalls', null, { root: true });

    let apiToken = null; // Это токен который необходимо вернуть и вписать в поле токена в модальное окно после запроса
    const params = rootGetters['integrations/getIntegrationParams']('hrm');

    if (params.access_token) {
      commit('STOP_INTERVAL_INTEGRATION_CHECK');
    }

    if (params.access_token) {
      await Axios.get(`${integrationHost}/v1/hrm/auth/status`, {
        headers: { Authorization: `Bearer ${params.access_token}` }
      })
        .then(async response => {
          commit(
            'integrations/SET_INTEGRATIONS_INSTALLS_STATUS',
            { code: 'hrm', value: response.data.ok },
            { root: true }
          );

          if (response.data.ok) {
            apiToken = await Axios.get(`${integrationHost}/v1/hrm/company`, {
              headers: { Authorization: `Bearer ${params.access_token}` }
            }).then(response => {
              commit('SET_HRM_INTEGRATION_STATUS', true);
              commit('STOP_INTERVAL_INTEGRATION_CHECK');
              commit('SET_IS_HRM_LOADING', false);

              return response.data.hrm_api_token;
            });
          } else {
            this._vm.$noty.error(window.$tr('integrations.need-valid-token'));
          }

          commit('SET_IS_HRM_LOADING', false);
        })
        .catch(() => {
          this._vm.$noty.error(window.$tr('common.error'));
        });
    }

    return apiToken;
  },

  // Периодические запросы к интеграционному сервису чтобы увидеть когда появится access_token в params
  startIntervalIntegrationCheckHrm({ commit, dispatch, state }) {
    const integrationInterval = setInterval(() => {
      if (state.integrationIntervalCount <= 6) {
        commit('INCREMENT_INTEGRATION_INTERVAL_COUNT');
        dispatch('grantAccessToHrm');
      } else {
        commit('STOP_INTERVAL_INTEGRATION_CHECK');
        commit('SET_IS_HRM_LOADING', false);
      }
    }, 1000);

    commit('SET_INTEGRATION_INTERVAL', integrationInterval);
  },

  async patchHrmApiToken({ commit, rootGetters }, apiToken) {
    commit('SET_IS_HRM_LOADING', true);

    const params = rootGetters['integrations/getIntegrationParams']('hrm');

    if (params.access_token) {
      const requestParams = { hrm_api_token: apiToken };

      await Axios.patch(`${integrationHost}/v1/hrm/company`, requestParams, {
        headers: { Authorization: `Bearer ${params.access_token}` }
      })
        .then(response => {
          if (response.data.active) {
            this._vm.$noty.success(window.$tr('integrations.token-accepted'));
          }
        })
        .catch(error => {
          if (error.response.status === 400) {
            this._vm.$noty.error(window.$tr('integrations.token-invalid'));
          }
        })
        .finally(() => {
          commit('SET_IS_HRM_LOADING', false);
        });
    }
  },

  async fetchHrmProjects({ commit, rootGetters }) {
    commit('SET_IS_HRM_LOADING', true);

    const params = rootGetters['integrations/getIntegrationParams']('hrm');
    const paginationString = stringifyPaginationV3(state.hrmProjectsPagination);
    const requestParams = {
      filter: {
        manager_id: state.projectsHrmFilters.author,
        title: state.projectsHrmFilters.title,
        region: state.projectsHrmFilters.region
      }
    };

    if (params.access_token) {
      await Axios.get(
        `${integrationHost}/v1/hrm/projects?with[]=manager&with[]=vacancy&order[by]=vacancy${paginationString}`,
        {
          headers: { Authorization: `Bearer ${params.access_token}` },
          params: requestParams,
          paramsSerializer: requestParams => qs.stringify(requestParams)
        }
      )
        .then(response => {
          if (response.status === 200) {
            const projects = response.data._embedded.projects.map(x => {
              return { ...x, isActive: x._embedded.vacancy !== undefined ? true : false };
            });

            commit('SET_HRM_PROJECTS', projects);

            if (response.data._page !== undefined) {
              commit('SET_HRM_PROJECTS_PAGINATION', { property: '_page', value: response.data._page });
            }
            if (response.data._page_count !== undefined) {
              commit('SET_HRM_PROJECTS_PAGINATION', { property: '_page_count', value: response.data._page_count });
            }
            if (response.data._total_items !== undefined) {
              commit('SET_HRM_PROJECTS_PAGINATION', { property: '_total_items', value: response.data._total_items });
            }
          }
        })
        .finally(() => {
          commit('SET_IS_HRM_LOADING', false);
        });
    }
  },

  async toggleHrmProject({ commit, rootGetters }, { cell, vacancyId }) {
    commit('SET_CONNECTION_LOADING', true);

    const params = rootGetters['integrations/getIntegrationParams']('hrm');

    if (params.access_token) {
      // Если вакансия не привязана, то ее значение null и тогда привязываем
      if (!cell.isActive) {
        const requestParams = {
          vcv_vacancy_id: Number(vacancyId),
          hrm_project_id: cell.hrm_project_id,
          active: true
        };

        await Axios.post(`${integrationHost}/v1/hrm/vacancies`, requestParams, {
          headers: { Authorization: `Bearer ${params.access_token}` }
        })
          .then(response => {
            if (response.status === 200) {
              commit('TOGGLE_HRM_TO_VACANCY', cell.id);
            }
          })
          .catch(error => {
            if (error.response.status === 400) {
              this._vm.$noty.error(window.$tr('integrations.token-invalid'));
            } else {
              this._vm.$noty.error('common.error', error);
            }
          })
          .finally(() => {
            commit('SET_CONNECTION_LOADING', false);
          });
      } else {
        await Axios.delete(`${integrationHost}/v1/hrm/vacancies/${cell._embedded.vacancy.id}`, {
          headers: { Authorization: `Bearer ${params.access_token}` }
        })
          .then(response => {
            if (response.status === 200) {
              commit('TOGGLE_HRM_TO_VACANCY', cell.id);
            }
          })
          .catch(error => {
            if (error.response.status === 400) {
              this._vm.$noty.error(window.$tr('integrations.token-invalid'));
            } else {
              this._vm.$noty.error('common.error', error);
            }
          })
          .finally(() => {
            commit('SET_CONNECTION_LOADING', false);
          });
      }
    }
  },

  async forceSync({ dispatch, rootGetters }) {
    const params = rootGetters['integrations/getIntegrationParams']('hrm');

    if (params.access_token) {
      this.isHrmLoading = true;

      await Axios.post(
        `${integrationHost}/v1/hrm/sync-projects`,
        {},
        { headers: { Authorization: `Bearer ${params.access_token}` } }
      );

      await dispatch('fetchHrmProjects');

      this.isHrmLoading = false;
    }
  },

  async fetchHrmManagers({ commit, rootGetters }) {
    const params = rootGetters['integrations/getIntegrationParams']('hrm');

    if (params.access_token) {
      await Axios.get(`${integrationHost}/v1/hrm/managers`, {
        headers: { Authorization: `Bearer ${params.access_token}` }
      }).then(response => {
        if (response.status === 200) {
          const managers = response.data._embedded.managers.map(x => ({ id: x.id, name: x.full_name }));

          commit('SET_HRM_MANAGERS', managers);
        }
      });
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
